'use client';

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { animate, motion, useDragControls, useMotionValue } from 'framer-motion';
import { GripHorizontal, RotateCcw, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTutorialOptional } from '@/features/tutorial/TutorialContext';

const STORAGE_PREFIX = 'takeover-lobby-panel-pos:';

/**
 * Global event that the lobby's "reset panels" control fires.
 * Every `DraggableFloatingPanel` listens and snaps itself back to the
 * default (layout) position, so a single user action recovers any panel
 * that drifted off-screen.
 */
export const PANELS_RESET_EVENT = 'takeover:panels:reset';

// Fallback bounds used only until the real viewport is measured — kept
// deliberately roomy so the first drag feels snappy; the per-frame clamp
// in `onDragEnd` guarantees the panel never stays off-screen regardless.
const FALLBACK_BOUNDS = {
  left: -1400,
  right: 1400,
  top: -500,
  bottom: 600,
} as const;

/** Minimum visible surface we keep on-screen after a drag (px). If the
 *  user overshoots and a panel is about to vanish, we animate it back so
 *  at least this much of it is visible on every edge. */
const MIN_VISIBLE_PX = 96;

interface DraggableFloatingPanelProps {
  /** Unique id used as a localStorage key so positions survive reloads. */
  id: string;
  children: ReactNode;
  /** Classes applied to the outer draggable wrapper. */
  className?: string;
  /** Which side of the panel the grip tab sits on. */
  handlePosition?: 'tr' | 'tl';
  /** When provided, renders an always-visible "X" close affordance next
   *  to the drag grip so users can dismiss the panel without hunting
   *  for the bottom panel-rail toggle or a hotkey. The handler is
   *  expected to be the same one bound to the rail's toggle for this
   *  panel id, so re-opening behaviour stays consistent. */
  onClose?: () => void;
}

/**
 * Wraps a floating HUD panel and makes it draggable by a small tab that
 * appears above the panel on hover. Positions persist to localStorage
 * per `id`. Double-click the tab to snap back to the default spot.
 *
 * ## Recovery guarantees
 *
 * Panels can never get "lost" outside the playable area:
 *
 *   1. On drag-end we measure the panel's bounding rect and, if any edge
 *      is outside the viewport by more than `MIN_VISIBLE_PX`, animate the
 *      offset back so the panel is once again reachable.
 *   2. The lobby footer exposes a "Reset panels" action that fires
 *      the `PANELS_RESET_EVENT` event — every panel listens and snaps
 *      back to its default layout position, wiping persisted offsets.
 */
export function DraggableFloatingPanel({
  id,
  children,
  className,
  handlePosition = 'tr',
  onClose,
}: DraggableFloatingPanelProps) {
  const storageKey = STORAGE_PREFIX + id;
  const dragControls = useDragControls();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const [hasCustomPosition, setHasCustomPosition] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Tutorial sessions render every panel at its scripted default
  // layout slot. The tutorial overlay's spotlight cutouts measure the
  // live DOM position, so a persisted offset from a previous regular-
  // lobby drag would put the spotlight on the wrong place and break
  // the choreography. We also skip the persistence write so a tutorial
  // session never clobbers the user's saved real-lobby layout.
  const tutorial = useTutorialOptional();
  const isTutorial = tutorial != null;

  useEffect(() => {
    if (isTutorial) return;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as { x?: number; y?: number };
        if (typeof parsed.x === 'number') x.set(parsed.x);
        if (typeof parsed.y === 'number') y.set(parsed.y);
        if (parsed.x || parsed.y) setHasCustomPosition(true);
      }
    } catch {
      // Corrupt / missing value — treat as default position.
    }
  }, [isTutorial, storageKey, x, y]);

  const persist = useCallback(() => {
    if (isTutorial) {
      // In-memory drag still respected for the current frame, but never
      // hits localStorage — tutorial layouts aren't user preferences.
      const px = x.get();
      const py = y.get();
      setHasCustomPosition(Math.abs(px) > 1 || Math.abs(py) > 1);
      return;
    }
    try {
      const px = x.get();
      const py = y.get();
      window.localStorage.setItem(storageKey, JSON.stringify({ x: px, y: py }));
      setHasCustomPosition(Math.abs(px) > 1 || Math.abs(py) > 1);
    } catch {
      // localStorage unavailable — position still honoured for this session.
    }
  }, [isTutorial, storageKey, x, y]);

  const reset = useCallback(() => {
    // Animate home rather than snapping so the user can actually see
    // where the panel went — helps when they triggered a global reset
    // from the footer and several panels are relocating at once.
    animate(x, 0, { type: 'spring', stiffness: 320, damping: 28 });
    animate(y, 0, { type: 'spring', stiffness: 320, damping: 28 });
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
    setHasCustomPosition(false);
  }, [storageKey, x, y]);

  /**
   * After a drag, measure where the panel ended up and nudge it back if
   * it's mostly off-screen. We clamp against the **document viewport**
   * rather than a parent container because the lobby HUD layer
   * spans `inset-0` — the viewport is always the effective stage.
   */
  const clampIntoView = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // How far past each edge is the panel? Positive = past the edge.
    const overflowLeft = -(rect.right - MIN_VISIBLE_PX);
    const overflowRight = rect.left + MIN_VISIBLE_PX - vw;
    const overflowTop = -(rect.bottom - MIN_VISIBLE_PX);
    const overflowBottom = rect.top + MIN_VISIBLE_PX - vh;

    let dx = 0;
    let dy = 0;
    if (overflowLeft > 0) dx = overflowLeft;
    else if (overflowRight > 0) dx = -overflowRight;
    if (overflowTop > 0) dy = overflowTop;
    else if (overflowBottom > 0) dy = -overflowBottom;

    if (dx !== 0) {
      animate(x, x.get() + dx, { type: 'spring', stiffness: 260, damping: 26 });
    }
    if (dy !== 0) {
      animate(y, y.get() + dy, { type: 'spring', stiffness: 260, damping: 26 });
    }
  }, [x, y]);

  // Listen for the global "reset all panels" event.
  useEffect(() => {
    const onReset = () => reset();
    window.addEventListener(PANELS_RESET_EVENT, onReset);
    return () => window.removeEventListener(PANELS_RESET_EVENT, onReset);
  }, [reset]);

  return (
    <motion.div
      ref={rootRef}
      // `data-draggable-root` marks the actually-translated motion.div
      // for consumers that need to measure the panel's visible
      // position (e.g. the tutorial spotlight). The HUD identity
      // (`data-hud-id`) is published one level *above* this element by
      // `LobbyClient`, on the AnimatePresence wrapper — but that
      // outer wrapper's bounding rect ignores this inner transform,
      // so a panel the user previously dragged would have the
      // spotlight land on its original layout slot rather than its
      // current visible position. Tagging the inner draggable lets
      // the spotlight fall through to here once it's resolved the
      // HUD id, so the cutout always wraps the panel where the user
      // actually sees it. Carrying the same `id` through as the
      // attribute value keeps debugging cheap (one search lands on
      // both the outer marker and the inner measurement target).
      data-draggable-root={id}
      className={cn('group/draggable', className)}
      drag
      dragControls={dragControls}
      dragListener={false}
      dragMomentum={false}
      dragElastic={0}
      dragConstraints={FALLBACK_BOUNDS}
      onDragEnd={() => {
        // Run clamping on the next frame so framer-motion has finished
        // committing the final x/y before we measure.
        requestAnimationFrame(() => {
          clampIntoView();
          persist();
        });
      }}
      style={{ x, y }}
    >
      <div className="relative">
        {children}
        {/*
          Grip + reset + close cluster. Sits permanently visible at
          low opacity so users can see at a glance that a panel can be
          dragged or hidden — the previous `opacity-0` until hover hid
          the entire affordance set, which surfaced as "I didn't know
          panels could be closed". Bumps to full opacity on hover for
          the polished feel.
        */}
        <div
          className={cn(
            'absolute -top-2 z-30 flex items-center gap-0.5 opacity-60 group-hover/draggable:opacity-100 transition-opacity',
            handlePosition === 'tr' && 'right-2',
            handlePosition === 'tl' && 'left-2',
          )}
        >
          {hasCustomPosition && (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                reset();
              }}
              title="Reset panel position"
              aria-label="Reset panel position"
              className="h-4 w-4 grid place-items-center rounded-full bg-black/80 border border-amber-500/30 text-zinc-400 hover:text-amber-100 hover:border-amber-400/60 transition-colors shadow-[0_2px_6px_rgba(0,0,0,0.6)]"
            >
              <RotateCcw className="h-2 w-2" />
            </button>
          )}
          <button
            type="button"
            onPointerDown={(e) => {
              e.stopPropagation();
              dragControls.start(e);
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              reset();
            }}
            title="Drag to move · double-click to reset"
            aria-label="Drag to reposition panel"
            className="px-1.5 h-4 flex items-center justify-center rounded-full bg-black/80 border border-amber-500/30 text-zinc-400 hover:text-amber-100 hover:border-amber-400/60 cursor-grab active:cursor-grabbing transition-colors shadow-[0_2px_6px_rgba(0,0,0,0.6)]"
          >
            <GripHorizontal className="h-2.5 w-3" />
          </button>
          {onClose && (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              title="Hide panel"
              aria-label="Hide panel"
              className="h-4 w-4 grid place-items-center rounded-full bg-black/80 border border-amber-500/30 text-zinc-400 hover:text-amber-100 hover:border-amber-400/60 transition-colors shadow-[0_2px_6px_rgba(0,0,0,0.6)]"
            >
              <X className="h-2.5 w-2.5" />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Fires the global "reset panel positions" event. Every mounted
 * `DraggableFloatingPanel` will animate back to its default layout spot
 * and drop its persisted offset. Safe to call from the client at any
 * time (e.g. from a UI button in the lobby footer).
 */
export function resetAllPanelPositions() {
  try {
    // Proactively wipe persisted state so a reload won't resurrect a
    // panel's old off-screen position.
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        window.localStorage.removeItem(key);
      }
    }
  } catch {
    // localStorage unavailable — still dispatch so in-memory state resets.
  }
  window.dispatchEvent(new CustomEvent(PANELS_RESET_EVENT));
}
